/**
 * Centralized Logging Utility
 * Replaces console.log statements with a proper logging service
 * that can be extended to send logs to monitoring services in production
 */

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogContext {
  [key: string]: unknown;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private isProduction = import.meta.env.PROD;

  /**
   * Log informational messages
   * Only logs in development mode
   */
  info(message: string, ...args: unknown[]): void {
    if (this.isDevelopment) {
      console.log(`[INFO] ${message}`, ...args);
    }
    // In production, could send to monitoring service
  }

  /**
   * Log warning messages
   * Always logs, but can be sent to monitoring in production
   */
  warn(message: string, ...args: unknown[]): void {
    console.warn(`[WARN] ${message}`, ...args);
    // In production, send to monitoring service (e.g., Sentry)
  }

  /**
   * Log error messages
   * Always logs and should be sent to monitoring service
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    console.error(`[ERROR] ${message}`, errorObj, context || '');
    
    // In production, send to monitoring service (e.g., Sentry)
    if (this.isProduction && typeof window !== 'undefined') {
      // Sentry integration would go here
      // import * as Sentry from '@sentry/react';
      // Sentry.captureException(errorObj, { extra: context });
    }
  }

  /**
   * Log debug messages
   * Only logs in development mode
   */
  debug(message: string, ...args: unknown[]): void {
    if (this.isDevelopment) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  }

  /**
   * Log with context for better debugging
   */
  logWithContext(level: LogLevel, message: string, context: LogContext): void {
    const contextStr = JSON.stringify(context, null, 2);
    
    switch (level) {
      case 'error':
        this.error(message, new Error(message), context);
        break;
      case 'warn':
        this.warn(`${message}\nContext: ${contextStr}`);
        break;
      case 'debug':
        this.debug(`${message}\nContext: ${contextStr}`);
        break;
      default:
        this.info(`${message}\nContext: ${contextStr}`);
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience functions
export const logInfo = (message: string, ...args: unknown[]) => logger.info(message, ...args);
export const logWarn = (message: string, ...args: unknown[]) => logger.warn(message, ...args);
export const logError = (message: string, error?: Error | unknown, context?: LogContext) =>
  logger.error(message, error, context);
export const logDebug = (message: string, ...args: unknown[]) => logger.debug(message, ...args);
